import IBAN from 'iban';

import App from './App';
import { IntlProviderProps } from './app/';
import { AppState } from './AppState';
import AppStateView from './AppStateView';
import {
	ApiV1DisapprovePostRequest,
	ApiV2CaseCaseIdDamagePatchRequest,
	CaseGetControlQueryResultValidationResult,
	CaseUpdateControlCommandResult,
	ControlConfiguration,
	DocumentFeature,
	EasyClaimAppState,
	EasyClaimCaseResponseDto,
	EasyClaimDocumentType,
	EasyClaimFrontendStep,
	FeedbackType,
	ValidationResult,
	VehicleType,
} from './easyclaim.api.client.typescript/src/index';
import {
	DefaultLocaleNames,
	DefaultLocaleType,
	messages,
} from './easyclaim.app.internationalization/index';
import { reportError } from './easyclaim.app.libs/reportError';
import {
	Image,
	ImageSelectionService,
	SelectState,
	ServiceError,
} from './easyclaim.app.services/index';

export default class AppService {
	app: App;
	private frontendStepsCache?: {
		frontendSteps: EasyClaimFrontendStep[] | null | undefined;
		stepsByAppState: Map<EasyClaimAppState, EasyClaimFrontendStep>;
	};

	constructor(app: App) {
		this.app = app;
	}

	public isCaseUploaded(): boolean {
		return this.app.state.easyClaimCase?.state === EasyClaimAppState.WaitForAudit;
	}

	public onApproveSimpleAudit = async () => {
		const approvalRequest: ApiV1DisapprovePostRequest = {
			approvalRequest: {
				caseId: this.app.state.easyClaimCase!.caseId!,
				bankAccountNumber: this.app.state.easyClaimCase!.bankAccountNumber,
				bankAccountOwnerName: this.app.state.easyClaimCase!.bankAccountOwnerName,
			},
		};

		try {
			await this.app.state.easyClaimService!.approve(approvalRequest);
		} catch (e) {
			const newState = Object.assign({}, this.app.state, {
				serviceErrors: (this.app.state.serviceErrors || []).concat([e as ServiceError]),
			});

			this.app.setState(newState);

			throw e;
		}

		this.app.setState({
			screen: EasyClaimAppState.Feedback,
		});
	};

	public onApproveAudit = async () => {
		const approvalRequest: ApiV1DisapprovePostRequest = {
			approvalRequest: {
				caseId: this.app.state.easyClaimCase!.caseId!,
				bankAccountNumber: this.app.state.easyClaimCase!.bankAccountNumber,
				bankAccountOwnerName: this.app.state.easyClaimCase!.bankAccountOwnerName,
			},
		};

		try {
			return await this.app.state.easyClaimService!.approve(approvalRequest);
		} catch (e) {
			const newState = Object.assign({}, this.app.state, {
				serviceErrors: (this.app.state.serviceErrors || []).concat([e as ServiceError]),
			});

			this.app.setState(newState);

			throw e;
		}
	};

	public tryGoToNextStep = async () => {
		await this.app.tryGoToNextStep();
	};

	public onApproveWithFeedback = async () => {
		const approvalRequest: ApiV1DisapprovePostRequest = {
			approvalRequest: {
				caseId: this.app.state.easyClaimCase!.caseId!,
				bankAccountNumber: this.app.state.easyClaimCase!.bankAccountNumber,
				bankAccountOwnerName: this.app.state.easyClaimCase!.bankAccountOwnerName,
			},
		};

		try {
			await this.app.tryGoToNextStep();
			if (this.isScreenBankDetailsCompleted()) {
				await this.app.state.easyClaimService!.approve(approvalRequest);
			}
		} catch (e) {
			const newState = Object.assign({}, this.app.state, {
				serviceErrors: (this.app.state.serviceErrors || []).concat([e as ServiceError]),
			});

			this.app.setState(newState);

			throw e;
		}
	};

	public onDisapproveSimpleAudit = async () => {
		const disapprovalRequest: ApiV1DisapprovePostRequest = {
			approvalRequest: {
				caseId: this.app.state.easyClaimCase!.caseId!,
				bankAccountNumber: this.app.state.easyClaimCase!.bankAccountNumber,
				bankAccountOwnerName: this.app.state.easyClaimCase!.bankAccountOwnerName,
			},
		};

		try {
			await this.app.state.easyClaimService!.disapprove(disapprovalRequest);
		} catch (e) {
			reportError(e, { context: 'AppService.onDisapproveSimpleAudit' });
			alert('Nie można odrzucić sprawy. Spróbuj później');
		}

		this.app.setState({
			screen: EasyClaimAppState.Feedback,
		});
	};

	public onDisapproveAudit = async () => {
		const disapprovalRequest: ApiV1DisapprovePostRequest = {
			approvalRequest: {
				caseId: this.app.state.easyClaimCase!.caseId!,
				bankAccountNumber: this.app.state.easyClaimCase!.bankAccountNumber,
				bankAccountOwnerName: this.app.state.easyClaimCase!.bankAccountOwnerName,
			},
		};

		try {
			return await this.app.state.easyClaimService!.disapprove(disapprovalRequest);
		} catch (e) {
			reportError(e, { context: 'AppService.onDisapproveAudit' });
			alert('Nie można odrzucić sprawy. Spróbuj później');
		}
	};

	public onDisapproveWithFeedback = async () => {
		const disapprovalRequest: ApiV1DisapprovePostRequest = {
			approvalRequest: {
				caseId: this.app.state.easyClaimCase!.caseId!,
				bankAccountNumber: this.app.state.easyClaimCase!.bankAccountNumber,
				bankAccountOwnerName: this.app.state.easyClaimCase!.bankAccountOwnerName,
			},
		};

		try {
			await this.app.state.easyClaimService!.disapprove(disapprovalRequest);
			this.app.setState({
				screen: EasyClaimAppState.OfferRejected,
			});
		} catch (e) {
			reportError(e, { context: 'AppService.onDisapproveWithFeedback' });
		}
	};

	public updateDamage = async () => {
		const appState = this.app.state;
		const caseId = appState.easyClaimCase?.caseId;
		const damageDescription = appState.easyClaimCase?.damage?.description;

		if (caseId) {
			const request: ApiV2CaseCaseIdDamagePatchRequest = {
				caseId: caseId,
				body: damageDescription ?? undefined,
			};

			await appState.easyClaimService!.updateDamage(request);
		}
	};

	private isImageUploaded = (image: Image) =>
		[SelectState.Uploading, SelectState.Uploaded].includes(image.selectState);

	public confirmPicturesUploaded = async () => {
		const appState = this.app.state;
		const caseId = this.app.state.easyClaimCase?.caseId;

		await appState.easyClaimService!.confirmPicturesUploaded(caseId!);
	};

	public sendFeedback = async () => {
		const feedbackComment = this.app.state.easyClaimCase!.feedbackComment
			? this.app.state.easyClaimCase!.feedbackComment
			: undefined;
		const feedbackRating = this.app.state.easyClaimCase!.feedbackRating
			? this.app.state.easyClaimCase!.feedbackRating
			: undefined;

		await this.app.state.easyClaimService!.sendFeedback(
			this.app.state.easyClaimCase!.caseId!,
			feedbackComment,
			feedbackRating
		);
	};

	public sendOfferFeedback = async () => {
		const feedbackComment = this.app.state.easyClaimCase!.offerFeedbackComment
			? this.app.state.easyClaimCase!.offerFeedbackComment
			: undefined;
		const feedbackRating = this.app.state.easyClaimCase!.offerFeedbackRating
			? this.app.state.easyClaimCase!.offerFeedbackRating
			: undefined;

		await this.app.state.easyClaimService!.sendFeedback(
			this.app.state.easyClaimCase!.caseId!,
			feedbackComment,
			feedbackRating,
			FeedbackType.Offer
		);
	};

	public sendQuestionsAnswers = async () => {
		const questions =
			this.app.state.cjQuestions.length > 0 ? this.app.state.cjQuestions : undefined;
		const caseId = this.app.state.easyClaimCase?.caseId;

		if (questions === undefined || caseId === undefined) return;

		if (!this.app.state.cjQuestionsModified) return;

		await this.app.state.easyClaimService!.sendCjQuestionsAnswers(caseId, questions);
		this.app.setState({ cjQuestionsModified: false });
	};

	private allImagesAreUploaded = (images: Image[]) =>
		images.every((i) =>
			[
				SelectState.Uploaded,
				SelectState.UnprocessableEntity,
				SelectState.UnsupportedMediaType,
			].includes(i.selectState)
		);
	private allImagesHaveReadState = (images: Image[]) =>
		images.every((i) => i.selectState === SelectState.Read);
	private allAdditionalImagesWasUploaded = () =>
		this.allImagesAreUploaded(this.app.state.additionalDocumentImages);
	private allDamagesImagesWasUploaded = () =>
		this.allImagesAreUploaded(this.app.state.additionalDamageImages);

	private getFrontendStepsByAppState = (): ReadonlyMap<
		EasyClaimAppState,
		EasyClaimFrontendStep
	> => {
		const frontendSteps = this.app.state.easyClaimCase?._configuration?.frontendSteps;
		let cache = this.frontendStepsCache;

		if (!cache || cache.frontendSteps !== frontendSteps) {
			const stepsByAppState = new Map<EasyClaimAppState, EasyClaimFrontendStep>();

			for (const step of frontendSteps ?? []) {
				if (step.appState !== undefined && !stepsByAppState.has(step.appState)) {
					stepsByAppState.set(step.appState, step);
				}
			}

			cache = { frontendSteps, stepsByAppState };
			this.frontendStepsCache = cache;
		}

		return cache.stepsByAppState;
	};

	private getStepConfiguration = (screen: EasyClaimAppState): EasyClaimFrontendStep | undefined =>
		this.getFrontendStepsByAppState().get(screen);

	private getFirstMatchingStepConfiguration = (
		screens: readonly EasyClaimAppState[]
	): EasyClaimFrontendStep | undefined =>
		this.app.state.easyClaimCase?._configuration?.frontendSteps?.find(
			(step) => step.appState !== undefined && screens.includes(step.appState)
		);

	private getControlsConfigurationsForScreen = (
		screen: EasyClaimAppState
	): ControlConfiguration[] | null | undefined =>
		this.getStepConfiguration(screen)?.controlsConfigurations;

	private getControlConfigurationById = (
		screen: EasyClaimAppState,
		controlId: string
	): ControlConfiguration | undefined =>
		this.getControlsConfigurationsForScreen(screen)?.find((c) => c.id === controlId);

	public isDocumentTypeImageControlRequired = (
		screen: EasyClaimAppState,
		documentType: EasyClaimDocumentType
	): boolean => {
		const controlsConfigurations = this.getControlsConfigurationsForScreen(screen);
		const controlConfiguration = controlsConfigurations?.find(
			(c) => c.documentType === documentType
		);
		const isRequired = !controlsConfigurations || !!controlConfiguration?.isRequired;

		return isRequired;
	};

	public isDocumentTypeImageControlVisible = (
		screen: EasyClaimAppState,
		documentType: EasyClaimDocumentType
	): boolean => {
		const controlsConfigurations = this.getControlsConfigurationsForScreen(screen);

		const controlConfiguration = controlsConfigurations?.find(
			(c) => c.documentType === documentType
		);
		const isVisible = !controlsConfigurations || !!controlConfiguration;

		return isVisible;
	};

	public isVehicleImageFrontLeftRequired = (screen: EasyClaimAppState): boolean =>
		this.isDocumentTypeImageControlVisible(screen, EasyClaimDocumentType.FrontLeftPicture);

	public isVehicleImageRearLeftRequired = (screen: EasyClaimAppState): boolean =>
		this.isDocumentTypeImageControlVisible(screen, EasyClaimDocumentType.RearLeftPicture);

	public isVehicleImageRearRightRequired = (screen: EasyClaimAppState): boolean =>
		this.isDocumentTypeImageControlVisible(screen, EasyClaimDocumentType.RearRightPicture);

	public isVehicleImageFrontRightRequired = (screen: EasyClaimAppState): boolean =>
		this.isDocumentTypeImageControlVisible(screen, EasyClaimDocumentType.FrontRightPicture);

	public isVinImageVisible = (screen: EasyClaimAppState): boolean =>
		this.isDocumentTypeImageControlVisible(screen, EasyClaimDocumentType.VinPicture);

	public isVinImageRequired = (screen: EasyClaimAppState): boolean =>
		this.isDocumentTypeImageControlRequired(screen, EasyClaimDocumentType.VinPicture);

	public isWindshieldImageVisible = (screen: EasyClaimAppState): boolean =>
		this.isDocumentTypeImageControlVisible(screen, EasyClaimDocumentType.WindshieldPicture);

	public isWindshieldImageRequired = (screen: EasyClaimAppState): boolean =>
		this.isDocumentTypeImageControlRequired(screen, EasyClaimDocumentType.WindshieldPicture);

	public isWindshieldImageCompleted = () => {
		return this.isImageSelectionCompleted(this.app.state.windshieldImage);
	};

	public isOverallDashboardImageVisible = (screen: EasyClaimAppState): boolean =>
		this.isDocumentTypeImageControlVisible(screen, EasyClaimDocumentType.OverallDashboardPicture);

	public isOverallDashboardImageRequired = (screen: EasyClaimAppState): boolean =>
		this.isDocumentTypeImageControlRequired(screen, EasyClaimDocumentType.OverallDashboardPicture);

	public isOverallDashboardImageCompleted = () => {
		return this.isImageSelectionCompleted(this.app.state.overallDashboardImage);
	};

	public isDashboardImageVisible = (screen: EasyClaimAppState): boolean =>
		this.isDocumentTypeImageControlVisible(screen, EasyClaimDocumentType.DashboardPicture);

	public isDashboardImageRequired = (screen: EasyClaimAppState): boolean => {
		if (this.isDashboardImageFlaggedAsManualInput()) {
			return false;
		}
		if (this.isDashboardImageFlaggedAsRetake()) {
			return true;
		}

		return this.isDocumentTypeImageControlRequired(screen, EasyClaimDocumentType.DashboardPicture);
	};

	public isDashboardImageCompleted() {
		return this.isImageSelectionCompleted(this.app.state.dashboardImage);
	}

	public isDashboardMileageTextInputVisible = (screen: EasyClaimAppState): boolean => {
		const controlsConfigurations = this.getControlsConfigurationsForScreen(screen);

		const dashboardMileageTextInputConfiguration = controlsConfigurations?.find(
			(c) => c.id === 'dashboardMileageTextInput'
		);
		const isDashboardMileageTextInputVisible =
			!controlsConfigurations || !!dashboardMileageTextInputConfiguration;

		return isDashboardMileageTextInputVisible;
	};

	public isDashboardMileageTextInputRequired = (screen: EasyClaimAppState): boolean => {
		if (this.app.state.easyClaimCase?._configuration?.optionalMileageTextInput === true) {
			return false;
		}

		const controlsConfigurations = this.getControlsConfigurationsForScreen(screen);

		const dashboardMileageTextInputConfiguration = controlsConfigurations?.find(
			(c) => c.id === 'dashboardMileageTextInput'
		);
		const isMaximumMileageUploadAttemptsExceeded = this.isDashboardImageFlaggedAsManualInput();

		const isDashboardMileageTextInputRequiredBySettings = this.isMileageUploadLimitEnabled()
			? false
			: !controlsConfigurations || dashboardMileageTextInputConfiguration?.isRequired;

		const isDashboardMileageTextInputRequired =
			isDashboardMileageTextInputRequiredBySettings || isMaximumMileageUploadAttemptsExceeded;

		return !!isDashboardMileageTextInputRequired;
	};

	public isDashboardMileageTextInputCompleted = () =>
		this.app.state.isDashboardMileageTextInputCompleted;

	public parseMileageStringToNumber(text: string) {
		const vehicleTypeId = this.app.state.easyClaimCase?.vehicle?.vehicleTypeId;
		const MILEAGE_REGEX: string = vehicleTypeId === VehicleType.Car ? '^[0-9]{0,6}$' : '^0*\\d+$';
		let mileage: number | null = null;

		if (text && new RegExp(MILEAGE_REGEX).test(text)) {
			mileage = parseInt(text, 10);
		}

		return mileage;
	}

	public parseProvidedMileageStringToNumber(text: string, mileage: number | null) {
		const vehicleTypeId = this.app.state.easyClaimCase?.vehicle?.vehicleTypeId;

		if (text == null && mileage == null) {
			return null;
		}

		const numberRegex: string = '^0*\\d+$';
		const MILEAGE_REGEX: string = vehicleTypeId === VehicleType.Car ? '^[0-9]{0,6}$' : '^0*\\d+$';
		let mileageProvided: number | null = null;

		if (text && new RegExp(numberRegex).test(text)) {
			const inputNumber = parseInt(text, 10);

			if (vehicleTypeId === VehicleType.Car && inputNumber > 999999) {
				mileageProvided = mileage;
			} else if (text && new RegExp(MILEAGE_REGEX).test(text)) {
				mileageProvided = parseInt(text, 10);
			}
		}

		return mileageProvided;
	}

	public isMileageValid = (
		appState: AppState,
		screen: EasyClaimAppState,
		mileage: number | string | null | undefined
	) => {
		let mileageValue: number | null = null;

		if (mileage) {
			if (typeof mileage === 'string') {
				mileageValue = this.parseMileageStringToNumber(mileage);
			} else {
				mileageValue = mileage;
			}
		}

		const { dashboardImage } = appState;
		const isDashboardImageRequired = this.isDashboardImageRequired(screen);
		const isManualInput = this.isDashboardImageFlaggedAsManualInput();
		// eslint-disable-next-line
		return (
			((isDashboardImageRequired && this.isImageSelectionCompleted(dashboardImage)) ||
				!isDashboardImageRequired) &&
			((mileageValue !== null && mileageValue > 0) ||
				(!isManualInput &&
					this.app.state.easyClaimCase?._configuration?.optionalMileageTextInput === true))
		);
	};

	public isDashboardMileageTextInputValidationVisible = (screen: EasyClaimAppState): boolean => {
		const controlConfiguration = this.getControlConfigurationById(
			screen,
			'dashboardMileageTextInput'
		);
		const isValidationVisible = !!controlConfiguration?.validationResults?.length;

		return isValidationVisible;
	};

	public getControlsConfigurations = (screen: EasyClaimAppState): ControlConfiguration[] =>
		this.getControlsConfigurationsForScreen(screen) || [];

	public getDashboardMileageTextValidationResults = (
		screen: EasyClaimAppState
	): CaseGetControlQueryResultValidationResult[] => {
		const controlConfiguration = this.getControlConfigurationById(
			screen,
			'dashboardMileageTextInput'
		);

		return controlConfiguration?.validationResults || [];
	};

	public getDashboardMileageTextValue = (screen: EasyClaimAppState): number | null => {
		const controlConfiguration = this.getControlConfigurationById(
			screen,
			'dashboardMileageTextInput'
		);
		const result = controlConfiguration?.value ? parseInt(controlConfiguration.value) : null;

		return result;
	};

	public getDashboardImageMileageValue = (): number | null => {
		const dashboardImageMileage =
			this.app.state.dashboardImage?.uploadResult?.documentFeatureValues?.mileage;

		return dashboardImageMileage || null;
	};

	public updateMileageOnCase = async (
		mileage: number | null
	): Promise<CaseUpdateControlCommandResult | null> => {
		const easyClaimCaseId = this.app.state.easyClaimCase?.caseId;
		const screen = this.app.state.screen;

		if (easyClaimCaseId && screen && mileage !== null) {
			const result = await this.app.state.easyClaimService?.updateScreenControl(
				easyClaimCaseId,
				screen,
				'dashboardMileageTextInput',
				mileage?.toString()
			);

			return result || null;
		}

		return null;
	};

	public isVehicleInformationCompleted = () => {
		return (
			this.isImageSelectionCompleted(this.app.state.registrationDocument) &&
			this.isImageSelectionCompleted(this.app.state.registrationDocument2)
		);
	};

	public IsAdditionalDocumentsCompleted = () =>
		!!this.app.state.additionalDocumentImage &&
		this.isImageUploaded(this.app.state.additionalDocumentImage) &&
		this.allAdditionalImagesWasUploaded();

	isVehicleImagesCompleted = () => {
		return (
			this.isImageSelectionCompleted(this.app.state.vehicleImageFrontLeft) &&
			this.isImageSelectionCompleted(this.app.state.vehicleImageRearRight)
		);
	};

	public isVehicleFrontPerspectiveImageCompleted = () =>
		this.isImageSelectionCompleted(this.app.state.vehicleImageFront);
	public isVehicleFrontLeftPerspectiveImageCompleted = () =>
		this.isImageSelectionCompleted(this.app.state.vehicleImageFrontLeft);
	public isVehicleFrontRightPerspectiveImageCompleted = () =>
		this.isImageSelectionCompleted(this.app.state.vehicleImageFrontRight);
	public isVehicleRightPerspectiveImageCompleted = () =>
		this.isImageSelectionCompleted(this.app.state.vehicleImageRight);
	public isVehicleRearPerspectiveImageCompleted = () =>
		this.isImageSelectionCompleted(this.app.state.vehicleImageRear);
	public isVehicleRearLeftPerspectiveImageCompleted = () =>
		this.isImageSelectionCompleted(this.app.state.vehicleImageRearLeft);
	public isVehicleRearRightPerspectiveImageCompleted = () =>
		this.isImageSelectionCompleted(this.app.state.vehicleImageRearRight);
	public isVehicleLeftPerspectiveImageCompleted = () =>
		this.isImageSelectionCompleted(this.app.state.vehicleImageLeft);

	isPolicyCheckVehicleImagesCompleted = () => {
		return (
			this.isVehicleFrontPerspectiveImageCompleted() &&
			this.isVehicleRightPerspectiveImageCompleted() &&
			this.isVehicleRearPerspectiveImageCompleted() &&
			this.isVehicleLeftPerspectiveImageCompleted()
		);
	};

	public isQuestionsCompleated = (): boolean => {
		return this.app.state.cjQuestions.every((q) => !q.required || q.answer !== undefined);
	};

	isFourDiagonalPerspectivesCompleted = (screen: EasyClaimAppState) => {
		if (this.isScreenControlsConfigured(screen)) {
			const isVehicleImageFrontLeftRequired = this.isVehicleImageFrontLeftRequired(screen);
			const isVehicleImageRearLeftRequired = this.isVehicleImageRearLeftRequired(screen);
			const isVehicleImageRearRightRequired = this.isVehicleImageRearRightRequired(screen);
			const isVehicleImageFrontRightRequired = this.isVehicleImageFrontRightRequired(screen);

			return (
				(!isVehicleImageFrontLeftRequired || this.isVehicleFrontLeftPerspectiveImageCompleted()) &&
				(!isVehicleImageRearLeftRequired || this.isVehicleRearLeftPerspectiveImageCompleted()) &&
				(!isVehicleImageRearRightRequired || this.isVehicleRearRightPerspectiveImageCompleted()) &&
				(!isVehicleImageFrontRightRequired || this.isVehicleFrontRightPerspectiveImageCompleted())
			);
		} else {
			return (
				this.isVehicleFrontLeftPerspectiveImageCompleted() &&
				this.isVehicleFrontRightPerspectiveImageCompleted() &&
				this.isVehicleRearLeftPerspectiveImageCompleted() &&
				this.isVehicleRearRightPerspectiveImageCompleted()
			);
		}
	};

	isScreenControlsConfigured = (screen: EasyClaimAppState): boolean =>
		!!this.getControlsConfigurationsForScreen(screen)?.length;

	isScreenDashboardMileageCompleted = () => {
		const screen = EasyClaimAppState.DashboardMileage;
		const isManualInput = this.isDashboardImageFlaggedAsManualInput();

		if (this.isDashboardImageFlaggedAsRetake()) {
			return false;
		}

		if (this.isScreenControlsConfigured(screen)) {
			const isDashboardImageRequired = this.isDashboardImageRequired(screen);
			const isDashboardMileageTextInputRequired =
				isManualInput || this.isDashboardMileageTextInputRequired(screen);

			return (
				(!isDashboardImageRequired || this.isDashboardImageCompleted()) &&
				(!isDashboardMileageTextInputRequired || this.isDashboardMileageTextInputCompleted())
			);
		} else {
			return (
				(isManualInput || this.isDashboardImageCompleted()) &&
				this.isDashboardMileageTextInputCompleted()
			);
		}
	};

	isScreenFourAdditionalPicturesCompleted = (screen: EasyClaimAppState) => {
		if (this.isScreenControlsConfigured(screen)) {
			const isOverallDashboardRequired = this.isOverallDashboardImageRequired(screen);
			const isDashboardImageRequired = this.isDashboardImageRequired(screen);
			const isWindshieldImageRequired = this.isWindshieldImageRequired(screen);
			const isVinImageRequired = this.isVinImageRequired(screen);
			const isDashboardMileageTextInputRequired = this.isDashboardMileageTextInputRequired(screen);

			return (
				(!isOverallDashboardRequired || this.isOverallDashboardImageCompleted()) &&
				(!isDashboardImageRequired || this.isDashboardImageCompleted()) &&
				(!isWindshieldImageRequired || this.isWindshieldImageCompleted()) &&
				(!isVinImageRequired || this.isVinPictureCompleted()) &&
				(!isDashboardMileageTextInputRequired || this.isDashboardMileageTextInputCompleted()) &&
				!this.isDashboardImageFlaggedAsRetake()
			);
		} else {
			return (
				this.isOverallDashboardImageCompleted() &&
				this.isDashboardImageCompleted() &&
				this.isDashboardMileageTextInputCompleted() &&
				this.isWindshieldImageCompleted() &&
				this.isVinPictureCompleted()
			);
		}
	};

	isScreenFourAdditionalDocumentsCompleted = (screen: EasyClaimAppState) => {
		if (this.isScreenControlsConfigured(screen)) {
			const isDniOutsideRequired = this.isDocumentTypeImageControlRequired(
				screen,
				EasyClaimDocumentType.DniOutside
			);
			const isDniInsideRequired = this.isDocumentTypeImageControlRequired(
				screen,
				EasyClaimDocumentType.DniInside
			);
			const isRegistrationDocumentRequired = this.isDocumentTypeImageControlRequired(
				screen,
				EasyClaimDocumentType.RegistrationDocument
			);
			const isRegistrationDocumentInsideRequired = this.isDocumentTypeImageControlRequired(
				screen,
				EasyClaimDocumentType.RegistrationDocumentInside
			);
			const isVehicleDatasheetInsideRequired = this.isDocumentTypeImageControlRequired(
				screen,
				EasyClaimDocumentType.VehicleDatasheetInside
			);
			const isVehicleDatasheetOutsideRequired = this.isDocumentTypeImageControlRequired(
				screen,
				EasyClaimDocumentType.VehicleDatasheetOutside
			);
			const isDriverLicenseInsideRequired = this.isDocumentTypeImageControlRequired(
				screen,
				EasyClaimDocumentType.DriverLicenseInside
			);
			const isDriverLicenseOutsideRequired = this.isDocumentTypeImageControlRequired(
				screen,
				EasyClaimDocumentType.DriverLicenseOutside
			);
			const isItvRequired = this.isDocumentTypeImageControlRequired(
				screen,
				EasyClaimDocumentType.ITV
			);

			return (
				(!isDniOutsideRequired || this.isImageSelectionCompleted(this.app.state.dniOutside)) &&
				(!isDniInsideRequired || this.isImageSelectionCompleted(this.app.state.dniInside)) &&
				(!isRegistrationDocumentRequired ||
					this.isImageSelectionCompleted(this.app.state.registrationDocument)) &&
				(!isRegistrationDocumentInsideRequired ||
					this.isImageSelectionCompleted(this.app.state.registrationDocument2)) &&
				(!isVehicleDatasheetInsideRequired ||
					this.isImageSelectionCompleted(this.app.state.vehicleDatasheetInside)) &&
				(!isVehicleDatasheetOutsideRequired ||
					this.isImageSelectionCompleted(this.app.state.vehicleDatasheetOutside)) &&
				(!isDriverLicenseInsideRequired ||
					this.isImageSelectionCompleted(this.app.state.driverLicenseInside)) &&
				(!isDriverLicenseOutsideRequired ||
					this.isImageSelectionCompleted(this.app.state.driverLicenseOutside)) &&
				(!isItvRequired || this.isImageSelectionCompleted(this.app.state.itv))
			);
		} else {
			return (
				this.isImageSelectionCompleted(this.app.state.dniOutside) &&
				this.isImageSelectionCompleted(this.app.state.dniInside) &&
				this.isImageSelectionCompleted(this.app.state.registrationDocument) &&
				this.isImageSelectionCompleted(this.app.state.registrationDocument2) &&
				this.isImageSelectionCompleted(this.app.state.vehicleDatasheetInside) &&
				this.isImageSelectionCompleted(this.app.state.vehicleDatasheetOutside) &&
				this.isImageSelectionCompleted(this.app.state.driverLicenseInside) &&
				this.isImageSelectionCompleted(this.app.state.driverLicenseOutside) &&
				this.isImageSelectionCompleted(this.app.state.itv)
			);
		}
	};

	isVehicleRegistrationDocumentCompleted = () => {
		const controls = this.getControlsConfigurations(EasyClaimAppState.VehicleRegistrationDocument);

		return controls.every(({ isRequired, documentType }) => {
			if (documentType === EasyClaimDocumentType.RegistrationDocument && !!isRequired)
				return this.isImageSelectionCompleted(this.app.state.registrationDocument);

			if (documentType === EasyClaimDocumentType.RegistrationDocumentInside && !!isRequired)
				return this.isImageSelectionCompleted(this.app.state.registrationDocument2);

			return true;
		});
	};

	isScreenBankDetailsCompleted = () => {
		const isBankStatementDocumentRequirted = this.isBankStatementDocumentRequired();
		const trimmedIban = this.app.state.easyClaimCase?.bankAccountNumber?.replaceAll(/\s/g, '');

		if (!this.app.state.easyClaimCase?.bankAccountNumber) {
			return false;
		}
		if (!this.isBankAccountNumberValid()) {
			return false;
		}
		if (!(trimmedIban?.substring(0, 2) === 'PT' && trimmedIban.length === 25)) {
			return false;
		}
		if (isBankStatementDocumentRequirted)
			return this.isImageSelectionCompleted(this.app.state.bankAccountStatement);

		return true;
	};

	public isBankStatementDocumentRequired = (): boolean => {
		return (
			this.app.state.retreivedBankAccountNumber?.replaceAll(/\s/g, '') !==
				this.app.state.easyClaimCase?.bankAccountNumber?.replaceAll(/\s/g, '') ||
			!this.app.state.easyClaimCase?.bankAccountNumber
		);
	};

	isVinPictureCompleted = () => {
		return this.isImageSelectionCompleted(this.app.state.vinImage);
	};

	public isSolvdDamageImagesCompleted = (appState: AppState) => {
		const isAdditionalDamageDetected = ImageSelectionService.additionalDamagesDetected(appState);
		const hasAnyAdditionalDamageImages = appState.additionalDamageImages.length > 0;
		const hasCloseUpDamageImage = this.isImageSelectionCompleted(appState.closeUpDamageImage);
		const hasOverallDamageImage = this.isImageSelectionCompleted(appState.overallDamageImage);
		const isAdditionalDamagePicturesRequired =
			appState.easyClaimCase?._configuration?.additionalDamagePicturesRequired ?? false;

		if (!hasAnyAdditionalDamageImages && !hasCloseUpDamageImage && !hasOverallDamageImage) {
			return !isAdditionalDamagePicturesRequired;
		}

		const hasEffectiveSideValue = appState.damageImagesToggle ?? isAdditionalDamageDetected;

		if (!hasEffectiveSideValue) return true;

		return hasCloseUpDamageImage || hasOverallDamageImage || hasAnyAdditionalDamageImages;
	};

	public computeDamageImagesToggle(appState: AppState): boolean | undefined {
		const wasAdditionalDamageDetected = ImageSelectionService.additionalDamagesDetected(appState);

		if (wasAdditionalDamageDetected) return true;

		if (appState.damageImagesToggle === undefined) return undefined;

		const hasEffectiveSideValue = wasAdditionalDamageDetected ? true : appState.damageImagesToggle;

		const hasAnyImage =
			ImageSelectionService.isAnyDamageImageLoading(appState) ||
			ImageSelectionService.isAnyDamageImageLoaded(appState) ||
			ImageSelectionService.additionalDamagesDetected(appState);

		return hasEffectiveSideValue || hasAnyImage;
	}

	public isDamageImagesCompleted = (screen: EasyClaimAppState) => {
		let isDamageRequired = false;

		if (this.isScreenControlsConfigured(screen)) {
			isDamageRequired = this.isDocumentTypeImageControlRequired(
				screen,
				EasyClaimDocumentType.DetailPicture
			);
		}

		return (
			(!isDamageRequired || this.isImageSelectionCompleted(this.app.state.closeUpDamageImage)) &&
			(!isDamageRequired || this.isImageSelectionCompleted(this.app.state.overallDamageImage)) &&
			this.allDamagesImagesWasUploaded()
		);
	};

	public isDamageDescriptionCompleted = () => {
		return (
			!!this.app.state.easyClaimCase &&
			!!this.app.state.easyClaimCase.damage &&
			!!this.app.state.easyClaimCase.damage.description
		);
	};

	public isImageSelectionCompleted(image?: Image): boolean {
		return ImageSelectionService.hasImageSuccessfullySelectionFinished(image);
	}

	private getVisibleConfiguredImages = (
		images: (Image | undefined)[],
		controlsConfigurations: ControlConfiguration[] | null | undefined
	): (Image | undefined)[] => {
		const configuredDocumentTypes = new Set(
			controlsConfigurations?.map((c) => c.documentType) ?? []
		);

		return images.filter((image) => configuredDocumentTypes.has(image?.document?.documentType));
	};

	public hasAnyWarningsOrErrors = (images: (Image | undefined)[]) =>
		images.some(
			(i) =>
				!!i &&
				!!i.uploadResult &&
				!!i.uploadResult.validationResults &&
				i.uploadResult.validationResults.some(
					(vr) =>
						(vr.validationResult === ValidationResult.Warning ||
							vr.validationResult === ValidationResult.Error) &&
						vr.feature !== DocumentFeature.ExpectedLimitedAmountOfAttempts &&
						vr.feature !== DocumentFeature.ExpectedSharpImage
				)
		);

	public hasAnyRegistrationDocumentWarningsOrErrors = () =>
		this.hasAnyWarningsOrErrors([
			this.app.state.registrationDocument,
			this.app.state.registrationDocument2,
		]);

	public hasAnyVehicleImagesWarningsOrErrors = (): boolean =>
		this.hasAnyWarningsOrErrors([
			this.app.state.vehicleImageFrontLeft,
			this.app.state.vehicleImageRearRight,
		]);

	public hasAnyPolicyCheckVehicleImagesWarningsOrErrors = (): boolean =>
		this.hasAnyWarningsOrErrors([
			this.app.state.vehicleImageFront,
			this.app.state.vehicleImageRear,
			this.app.state.vehicleImageLeft,
			this.app.state.vehicleImageRight,
		]);

	public hasAnyFourDiagonalPerspectivesWarningsOrErrors = (): boolean => {
		const images = [
			this.app.state.vehicleImageFrontLeft,
			this.app.state.vehicleImageRearLeft,
			this.app.state.vehicleImageFrontRight,
			this.app.state.vehicleImageRearRight,
		];
		const controlsConfigurations = this.getStepConfiguration(
			EasyClaimAppState.FourDiagonalPerspectives
		)?.controlsConfigurations;
		const visibleImages = this.getVisibleConfiguredImages(images, controlsConfigurations);

		return this.hasAnyWarningsOrErrors(visibleImages);
	};

	public hasAnyFourAdditionalPicturesWarningsOrErrors = (): boolean => {
		let images: (Image | undefined)[];

		if (this.isDashboardImageFlaggedAsManualInput())
			images = [
				this.app.state.overallDashboardImage,
				this.app.state.windshieldImage,
				this.app.state.vinImage,
			];
		else
			images = [
				this.app.state.overallDashboardImage,
				this.app.state.dashboardImage,
				this.app.state.windshieldImage,
				this.app.state.vinImage,
			];

		const controlsConfigurations = this.getFirstMatchingStepConfiguration([
			EasyClaimAppState.FourAdditionalPicturesAZCEE,
			EasyClaimAppState.FourAdditionalPictures,
		])?.controlsConfigurations;
		const visibleImages = this.getVisibleConfiguredImages(images, controlsConfigurations);

		return this.hasAnyWarningsOrErrors(visibleImages);
	};

	public hasAnyFourAdditionalDocumentsWarningsOrErrors = (): boolean =>
		this.hasAnyWarningsOrErrors([
			this.app.state.registrationDocument,
			this.app.state.registrationDocument2,
			this.app.state.dniOutside,
			this.app.state.dniInside,
			this.app.state.driverLicenseOutside,
			this.app.state.driverLicenseInside,
			this.app.state.vehicleDatasheetOutside,
			this.app.state.vehicleDatasheetInside,
		]);

	public hasAnyVehicleRegistrationDocumentWarningsOrErrors = (
		screen: EasyClaimAppState
	): boolean => {
		const documentTypes = this.getControlsConfigurations(screen).map(
			({ documentType }) => documentType
		);
		const images = [
			documentTypes.includes(EasyClaimDocumentType.RegistrationDocument)
				? this.app.state.registrationDocument
				: undefined,
			documentTypes.includes(EasyClaimDocumentType.RegistrationDocumentInside)
				? this.app.state.registrationDocument2
				: undefined,
		];

		return this.hasAnyWarningsOrErrors(images);
	};

	public hasAnyVinPictureWarningsOrErrors = (): boolean =>
		this.hasAnyWarningsOrErrors([this.app.state.vinImage]);

	public hasAnyDocumentPictureWarningsOrErrors = (): boolean =>
		this.hasAnyWarningsOrErrors([
			this.app.state.additionalDocumentImage,
			...this.app.state.additionalDocumentImages,
		]);

	public hasAnyDamageWarningsOrErrors = (): boolean =>
		this.hasAnyWarningsOrErrors([
			this.app.state.overallDamageImage,
			this.app.state.closeUpDamageImage,
			...this.app.state.additionalDamageImages,
		]);

	public isBankAccountOwnerValid = () => {
		return (
			!!this.app.state.easyClaimCase &&
			!!this.app.state.easyClaimCase.bankAccountOwnerName &&
			this.app.state.easyClaimCase.bankAccountOwnerName.length >= 1
		);
	};

	public hasMileageInconsistency = (screen: EasyClaimAppState | undefined): boolean => {
		if (screen === undefined) return false;
		const result = this.getDashboardMileageTextValidationResults(screen);

		return (
			result?.some(
				(validations) =>
					validations.feature === DocumentFeature.MileageMustBeConsistent &&
					validations.validationResult != ValidationResult.Correct
			) ?? false
		);
	};

	private ibanPrefix = 'PL';
	public isBankAccountNumberSpecified = () => {
		return !!this.app.state.easyClaimCase && !!this.app.state.easyClaimCase.bankAccountNumber;
	};

	public isBankAccountNumberValid = () => {
		const trimmedIban = this.app.state.easyClaimCase?.bankAccountNumber?.replaceAll(/\s/g, '');

		return (
			!!this.app.state.easyClaimCase &&
			!!this.app.state.easyClaimCase.bankAccountNumber &&
			IBAN.isValid(trimmedIban)
		);
	};

	public updateCaseBankAccount = (value: string) => {
		const easyClaimCase = this.app.state.easyClaimCase!;

		easyClaimCase.bankAccountNumber = IBAN.printFormat(this.ibanPrefix + value, ' ').substring(
			this.ibanPrefix.length
		);
		this.app.setState({ easyClaimCase });
	};

	public isCalculationFileDownloadingAllowed = (appState: AppState) =>
		appState.easyClaimCase?._configuration?.allowDownloadCalculationFile === true;

	public isCalculationFileDownloadingAllowedOnFinishedView = (appState: AppState) =>
		appState.easyClaimCase?._configuration?.allowDownloadCalculationFileOnFinishedView === true;

	public setPrivacyDataAccepted = async () => {
		try {
			await this.app.state.easyClaimService!.setPrivacyDataAccepted(
				this.app.state.easyClaimCase!.caseId!
			);
		} catch (e) {
			const newState = Object.assign({}, this.app.state, {
				serviceErrors: (this.app.state.serviceErrors || []).concat([e as ServiceError]),
			});

			this.app.setState(newState);

			throw e;
		}
	};

	public isItTestCase = () => !!this.app?.state?.easyClaimCase?.isTest;

	public getIntlConfig(): IntlProviderProps {
		const { appParameters } = this.app.state;

		const lang = appParameters.lang;
		const locale: DefaultLocaleType =
			!!lang && DefaultLocaleNames.includes(lang) ? (lang as DefaultLocaleType) : 'en';
		const messageType = this.determineMessageType(locale);

		return {
			locale: locale,
			messages: messages[messageType],
		};
	}

	private determineMessageType(locale: DefaultLocaleType): keyof typeof messages {
		const appState = this.app.state;
		const companyShortName = appState.easyClaimCase?._configuration?.companyShortName;
		const productType = appState.easyClaimCase?._configuration?.layout?.toLocaleLowerCase();
		const specialTranslation = appState.easyClaimCase?.specialTranslation?.toLocaleLowerCase();

		const translationMode = companyShortName
			? `${locale}_${companyShortName.toLowerCase()}`
			: appState.appParameters.lang;
		const productTypeTranslationMode = productType
			? `${translationMode}_${productType}`
			: translationMode;
		const specialTranslationMode = specialTranslation
			? `${translationMode}_${specialTranslation}`
			: undefined;

		const messagesTypes = Object.getOwnPropertyNames(messages);

		let messageType: keyof typeof messages = messagesTypes.includes(translationMode || '')
			? (translationMode as keyof typeof messages)
			: locale;

		messageType = messagesTypes.includes(productTypeTranslationMode)
			? (productTypeTranslationMode as keyof typeof messages)
			: messageType;

		messageType =
			specialTranslationMode && messagesTypes.includes(specialTranslationMode)
				? (specialTranslationMode as keyof typeof messages)
				: messageType;

		return messageType;
	}

	public loadEasyClaimCase = async (): Promise<EasyClaimCaseResponseDto | null> => {
		let result: EasyClaimCaseResponseDto | null = null;
		const appParameters = this.app.state.appParameters;
		const easyClaimService = this.app.state.easyClaimService;

		if (easyClaimService === undefined) {
			return null;
		} else {
			const shortLinkId = appParameters.shortLinkId;

			if (shortLinkId) {
				result = await easyClaimService.getCaseByShortLinkId(shortLinkId);
			}

			if (result && result.caseId) {
				await easyClaimService.saveStartCustomerJourneyEvent(
					result.caseId,
					JSON.stringify(appParameters)
				);
			}

			return result ? removeNullAndUndefinedFields(result) : result;
		}
	};

	public isMileageUploadLimitEnabled = (): boolean => {
		return (
			(this.app.state.easyClaimCase?._configuration
				?.maximumMileagePhotoUploadAttemptsBeforeManualInput ?? 0) > 0
		);
	};

	public isDashboardImageFlaggedAsManualInput = (): boolean => {
		return !!this.app.state.dashboardImage?.uploadResult?.validationResults?.find(
			(x) => x.feature === DocumentFeature.ExpectedMileageManualInput
		);
	};

	public isDashboardImageFlaggedAsRetake = (): boolean => {
		return !!this.app.state.dashboardImage?.uploadResult?.validationResults?.find(
			(x) => x.feature === DocumentFeature.ExpectedMileageRetake
		);
	};

	public getCurrentStateScreenRenderer = (): AppStateView | null => {
		const appState = this.app.state;

		return (
			appState.appStateViews.find(
				(stateComponent) => stateComponent.appState === appState.screen
			) ?? null
		);
	};
}

function removeNullAndUndefinedFields<T>(obj: T): T {
	const replacer = (_key: string, value: unknown) => (value === null ? undefined : value);

	return JSON.parse(JSON.stringify(obj, replacer)) as T;
}
